import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiMail, FiUser, FiLock, FiArrowRight, FiAlertCircle, FiDownload, FiSmartphone, FiShield, FiRefreshCw } from 'react-icons/fi';
import Logo from '../components/Logo';
import { useSite } from '../context/SiteContext';
import { playLogin, playError } from '../utils/sounds';

const API = process.env.REACT_APP_API_URL || '';

export default function Login() {
  const { login, verifyOtp, resendOtp, companyCode, companyName, setCompanyCode } = useAuth();
  // "This is a public device" checkbox — when checked, we tell AuthContext
  // to write the session token to sessionStorage so it evaporates when
  // the browser tab closes.
  const [publicDevice, setPublicDevice] = useState(false);
  // Second-factor OTP state. otpChallenge is set once the server tells
  // us the password was correct but an email code is needed to finish.
  const [otpChallenge, setOtpChallenge] = useState(null); // { otp_token, otp_email_hint, otp_expires_in }
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpResending, setOtpResending] = useState(false);
  const [otpResentAt, setOtpResentAt] = useState(0);
  const site = useSite();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [authMode, setAuthMode] = useState('local');
  const [loginTab, setLoginTab] = useState('local'); // for hybrid mode
  // First-run setup: a fresh standalone/hybrid server with no admin yet
  // shows a create-admin form instead of the login form.
  const [needsSetup, setNeedsSetup] = useState(false);
  const [setupForm, setSetupForm] = useState({ name: '', email: '', confirmEmail: '' });
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupDone, setSetupDone] = useState(false);
  const [setupError, setSetupError] = useState('');

  useEffect(() => {
    const headers = {};
    if (companyCode) headers['X-Company-Code'] = companyCode;
    fetch(`${API}/api/auth/mode`, { headers })
      .then(r => r.json())
      .then(data => {
        setAuthMode(data.mode || 'local');
        if (data.mode === 'ad') setLoginTab('ad');
        setNeedsSetup(!!data.needsSetup);
      })
      .catch(() => {});
  }, [companyCode]);

  const handleSetup = async (e) => {
    e.preventDefault();
    setSetupError('');
    if (setupForm.email.trim().toLowerCase() !== setupForm.confirmEmail.trim().toLowerCase()) {
      setSetupError('Emails do not match'); return;
    }
    setSetupLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (companyCode) headers['X-Company-Code'] = companyCode;
      const res = await fetch(`${API}/api/auth/setup`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ name: setupForm.name.trim(), email: setupForm.email.trim().toLowerCase() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setSetupError(data.error || 'Setup failed'); setSetupLoading(false); playError(); return; }
      playLogin();
      setSetupDone(true);
    } catch (ex) {
      setSetupError('Network error — is the server reachable?');
      playError();
    }
    setSetupLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const useAd = authMode === 'ad' || (authMode === 'hybrid' && loginTab === 'ad');
      const result = useAd
        ? await login(null, password, username, 'ad', { publicDevice })
        : await login(email, password, null, null, { publicDevice });
      if (result?.otp_required) {
        // Password accepted but the account uses email-code 2FA. Move
        // to the code entry step; keep publicDevice for the final call.
        setOtpChallenge({
          otp_token: result.otp_token,
          otp_email_hint: result.otp_email_hint,
          otp_expires_in: result.otp_expires_in,
          otp_email_sent: result.otp_email_sent,
        });
        setOtpCode('');
        setOtpError('');
      } else {
        playLogin();
      }
    } catch (err) {
      setError(err.message);
      playError();
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpVerifying(true);
    try {
      await verifyOtp(otpChallenge.otp_token, otpCode.trim(), { publicDevice });
      playLogin();
    } catch (err) {
      setOtpError(err.message);
      playError();
      // If the challenge expired (401), send them back to the password step.
      if (err.status === 401) setOtpChallenge(null);
    }
    setOtpVerifying(false);
  };

  const handleResendOtp = async () => {
    setOtpError('');
    setOtpResending(true);
    try {
      const fresh = await resendOtp(otpChallenge.otp_token);
      setOtpChallenge({
        otp_token: fresh.otp_token,
        otp_email_hint: fresh.otp_email_hint,
        otp_expires_in: fresh.otp_expires_in,
        otp_email_sent: fresh.otp_email_sent,
      });
      setOtpResentAt(Date.now());
    } catch (err) {
      setOtpError(err.message);
    }
    setOtpResending(false);
  };

  const cancelOtp = () => {
    setOtpChallenge(null);
    setOtpCode('');
    setOtpError('');
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (companyCode) headers['X-Company-Code'] = companyCode;
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      setForgotMsg(data.message || 'If an account exists with that email, a reset link has been sent.');
    } catch {
      setForgotMsg('If an account exists with that email, a reset link has been sent.');
    }
    setForgotLoading(false);
  };

  const showUsernameField = authMode === 'ad' || (authMode === 'hybrid' && loginTab === 'ad');
  const showEmailField = authMode === 'local' || (authMode === 'hybrid' && loginTab === 'local');
  const showForgotLink = authMode === 'local' || (authMode === 'hybrid' && loginTab === 'local');

  // ── First-run setup screen — shown on a fresh standalone/hybrid server
  // with no administrator yet. Creates the first admin account. ──
  if (needsSetup) {
    const canSubmit = setupForm.name.trim() && setupForm.email.trim() && setupForm.confirmEmail.trim()
      && setupForm.email.trim().toLowerCase() === setupForm.confirmEmail.trim().toLowerCase();
    return (
      <div className="gradient-bg" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflow: 'hidden' }}>
        <motion.div initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.5, type: 'spring' }}
          style={{ width: '100%', maxWidth: 440, background: 'var(--bg-card)', borderRadius: 20, padding: 'clamp(24px, 5vw, 44px) clamp(20px, 4vw, 36px)', border: '1px solid var(--border)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ margin: '0 auto 16px', width: 64, height: 64 }}>
              {site?.logoUrl ? <img src={site.logoUrl} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'contain' }} /> : <Logo size={64} />}
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)' }}>{site?.site_name || 'Asset Logic'}</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              {setupDone ? 'Almost there' : "Let's set up your administrator account"}
            </p>
          </div>

          {setupDone ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(22,163,74,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>📧</div>
              <p style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Check your email</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
                We've sent an activation link to <strong>{setupForm.email.trim().toLowerCase()}</strong>. Click it to set your password — you'll sign in with full administrator access.
              </p>
              <button type="button" onClick={() => { setSetupDone(false); setNeedsSetup(false); }}
                style={{ marginTop: 20, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
                Go to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSetup}>
              <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 20, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.18)', color: 'var(--text-secondary)', fontSize: 12.5, lineHeight: 1.5 }}>
                This server has no administrator yet. Create the first account — it will be granted full admin access once you activate it.
              </div>
              {setupError && (
                <div style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiAlertCircle size={16} /> {setupError}
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Full Name</label>
                <div style={{ position: 'relative' }}>
                  <FiUser size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="text" value={setupForm.name} onChange={e => setSetupForm(f => ({ ...f, name: e.target.value }))} required placeholder="Your name" autoFocus
                    style={{ width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <FiMail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="email" value={setupForm.email} onChange={e => setSetupForm(f => ({ ...f, email: e.target.value.toLowerCase() }))} required placeholder="you@example.com"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    style={{ width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Confirm Email</label>
                <div style={{ position: 'relative' }}>
                  <FiMail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="email" value={setupForm.confirmEmail} onChange={e => setSetupForm(f => ({ ...f, confirmEmail: e.target.value.toLowerCase() }))} required placeholder="Re-enter your email"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    style={{ width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10, border: `1px solid ${setupForm.confirmEmail && setupForm.confirmEmail !== setupForm.email ? '#dc2626' : 'var(--border)'}`, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                {setupForm.confirmEmail && setupForm.confirmEmail !== setupForm.email && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Emails do not match</div>}
              </div>
              <motion.button type="submit" disabled={setupLoading || !canSubmit}
                whileHover={canSubmit ? { scale: 1.02 } : {}} whileTap={canSubmit ? { scale: 0.98 } : {}}
                style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 600,
                  cursor: (setupLoading || !canSubmit) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: '0 4px 12px rgba(37,99,235,0.3)', opacity: (setupLoading || !canSubmit) ? 0.5 : 1 }}>
                {setupLoading ? 'Creating...' : <>Create admin account <FiArrowRight size={16} /></>}
              </motion.button>
            </form>
          )}

          {companyCode && (
            <p style={{ marginTop: 18, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
              {companyName || companyCode}
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="gradient-bg" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflow: 'hidden'
    }}>
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          animate={{ y: [-20, 20, -20], x: [-10, 10, -10], opacity: [0.05, 0.15, 0.05] }}
          transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.5 }}
          style={{
            position: 'absolute', display: window.innerWidth < 500 ? 'none' : 'block',
            width: 100 + i * 30, height: 100 + i * 30,
            borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)',
            top: `${10 + i * 12}%`, left: `${5 + i * 15}%`
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, type: 'spring' }}
        style={{
          width: '100%', maxWidth: 420, background: 'var(--bg-card)',
          borderRadius: 20, padding: 'clamp(24px, 5vw, 48px) clamp(20px, 4vw, 36px)', border: '1px solid var(--border)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', position: 'relative', zIndex: 1
        }}
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          style={{ textAlign: 'center', marginBottom: 32 }}
        >
          <div style={{ margin: '0 auto 16px', width: 64, height: 64 }}>
            {site?.logoUrl ? (
              <img src={site.logoUrl} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'contain' }} />
            ) : (
              <Logo size={64} />
            )}
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)' }}>{site?.site_name || 'Asset Logic'}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Sign in to your account</p>
          {companyCode && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              {companyName || companyCode}
              <button type="button"
                onClick={() => { setCompanyCode(''); }}
                style={{ marginLeft: 8, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', padding: 0 }}>
                Change company
              </button>
            </div>
          )}
        </motion.div>

        {/* Hybrid mode tabs */}
        {authMode === 'hybrid' && (
          <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <button
              type="button"
              onClick={() => { setLoginTab('local'); setError(''); }}
              style={{
                flex: 1, padding: '10px 12px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: loginTab === 'local' ? '#2563eb' : 'var(--bg-input)',
                color: loginTab === 'local' ? '#fff' : 'var(--text-secondary)',
                transition: 'all 0.2s'
              }}
            >
              Email Login
            </button>
            <button
              type="button"
              onClick={() => { setLoginTab('ad'); setError(''); }}
              style={{
                flex: 1, padding: '10px 12px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: loginTab === 'ad' ? '#2563eb' : 'var(--bg-input)',
                color: loginTab === 'ad' ? '#fff' : 'var(--text-secondary)',
                borderLeft: '1px solid var(--border)',
                transition: 'all 0.2s'
              }}
            >
              Domain Login
            </button>
          </div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 20,
              background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)',
              color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            <FiAlertCircle size={16} /> {error}
          </motion.div>
        )}

        {otpChallenge ? (<>
          <form onSubmit={handleVerifyOtp}>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <FiShield size={24} color="#2563eb" />
              </div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Check your email</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                We sent a 4-digit code to {otpChallenge.otp_email_hint || 'your inbox'}. Enter it below to finish signing in.
              </p>
              {otpResentAt > 0 && Date.now() - otpResentAt < 4000 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>New code sent.</div>
              )}
            </motion.div>

            <input autoFocus inputMode="numeric" maxLength={4} value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              style={{
                width: '100%', padding: '18px', borderRadius: 12,
                border: `1px solid ${otpError ? '#dc2626' : 'var(--border)'}`,
                background: 'var(--bg-input)', color: 'var(--text-primary)',
                fontSize: 32, fontWeight: 800, letterSpacing: '16px', textAlign: 'center',
                fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box', marginBottom: 14,
              }} />

            {otpError && (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiAlertCircle size={14} /> {otpError}
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={publicDevice} onChange={e => setPublicDevice(e.target.checked)}
                style={{ marginTop: 3, width: 15, height: 15, accentColor: '#2563eb', cursor: 'pointer' }} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>This is a public device — sign me out when the browser closes.</span>
            </label>

            <button type="submit" disabled={otpVerifying || otpCode.length !== 4}
              style={{
                width: '100%', padding: 14, borderRadius: 10, border: 'none', color: '#fff', fontSize: 15, fontWeight: 700,
                background: (otpVerifying || otpCode.length !== 4) ? '#94a3b8' : '#2563eb',
                cursor: (otpVerifying || otpCode.length !== 4) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: (otpVerifying || otpCode.length !== 4) ? 'none' : '0 4px 12px rgba(37,99,235,0.3)',
                marginBottom: 10,
              }}>
              {otpVerifying ? 'Verifying…' : <>Verify code <FiArrowRight size={16} /></>}
            </button>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
              <button type="button" onClick={cancelOtp}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: 6, textDecoration: 'underline' }}>
                Back to sign in
              </button>
              <button type="button" onClick={handleResendOtp} disabled={otpResending}
                style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: otpResending ? 'not-allowed' : 'pointer', padding: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <FiRefreshCw size={11} /> {otpResending ? 'Sending…' : 'Resend code'}
              </button>
            </div>
          </form>
        </>) : (<>
        <form onSubmit={handleSubmit}>
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
            style={{ marginBottom: 16 }}
          >
            {showEmailField && (
              <>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <FiMail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value.toLowerCase())} required={showEmailField}
                    placeholder="Enter your email"
                    autoFocus={typeof window !== 'undefined' && window.innerWidth >= 768 && !/iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent || '')}
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    style={{
                      width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10,
                      border: '1px solid var(--border)', background: 'var(--bg-input)',
                      color: 'var(--text-primary)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = '#2563eb'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>
              </>
            )}
            {showUsernameField && (
              <>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
                  Username
                </label>
                <div style={{ position: 'relative' }}>
                  <FiUser size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="text" value={username} onChange={e => setUsername(e.target.value.toLowerCase())} required={showUsernameField}
                    placeholder="Enter your username"
                    autoFocus={typeof window !== 'undefined' && window.innerWidth >= 768 && !/iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent || '')}
                    autoComplete="username"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    style={{
                      width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10,
                      border: '1px solid var(--border)', background: 'var(--bg-input)',
                      color: 'var(--text-primary)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = '#2563eb'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>
              </>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
            style={{ marginBottom: 24 }}
          >
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)} required
                placeholder="Enter your password"
                style={{
                  width: '100%', padding: '14px 14px 14px 42px', borderRadius: 10,
                  border: '1px solid var(--border)', background: 'var(--bg-input)',
                  color: 'var(--text-primary)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
          </motion.div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={publicDevice} onChange={e => setPublicDevice(e.target.checked)}
              style={{ marginTop: 3, width: 15, height: 15, accentColor: '#2563eb', cursor: 'pointer' }} />
            <span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>This is a public device</span>
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                Log me out automatically when I close this browser.
              </span>
            </span>
          </label>

          <motion.button
            type="submit" disabled={loading || !password || (showUsernameField ? !username : !email)}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            style={{
              width: '100%', padding: '14px', borderRadius: 10, border: 'none',
              background: '#2563eb',
              color: '#fff', fontSize: 15, fontWeight: 600,
              cursor: (loading || !password || (showUsernameField ? !username : !email)) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
              opacity: (loading || !password || (showUsernameField ? !username : !email)) ? 0.5 : 1
            }}
          >
            {loading ? 'Signing in...' : <>Sign In <FiArrowRight size={16} /></>}
          </motion.button>

          {showForgotLink && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" onClick={() => { setShowForgot(true); setForgotMsg(''); setForgotEmail(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {showUsernameField && !showForgotLink && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                Password is managed by your organisation's Active Directory
              </span>
            </div>
          )}
        </form>
        </>)}

        {showForgot && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            style={{ marginTop: 24, padding: '20px', borderRadius: 12, background: 'var(--bg-primary)', border: '1px solid var(--border)' }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Reset Password</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>Enter your email and we'll send you a reset link.</p>
            {forgotMsg ? (
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)', color: '#16a34a', fontSize: 13, marginBottom: 12 }}>
                {forgotMsg}
              </div>
            ) : (
              <form onSubmit={handleForgot}>
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <FiMail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value.toLowerCase())} required
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    placeholder="Enter your email"
                    style={{
                      width: '100%', padding: '12px 14px 12px 42px', borderRadius: 10,
                      border: '1px solid var(--border)', background: 'var(--bg-input)',
                      color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button type="submit" disabled={forgotLoading} style={{
                  width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                  background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', opacity: forgotLoading ? 0.7 : 1
                }}>
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}
            <button onClick={() => setShowForgot(false)} style={{
              background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12,
              cursor: 'pointer', marginTop: 10, display: 'block', width: '100%', textAlign: 'center'
            }}>Cancel</button>
          </motion.div>
        )}

        {/* Download Android app — always serves the latest APK. */}
        <motion.a
          href={`${API}/api/download/android`}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.02, boxShadow: '0 10px 28px rgba(16,185,129,0.4)' }}
          whileTap={{ scale: 0.98 }}
          style={{
            marginTop: 24, display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 18px', borderRadius: 14, textDecoration: 'none',
            background: 'linear-gradient(135deg, #059669, #10b981)',
            boxShadow: '0 6px 18px rgba(16,185,129,0.32)',
          }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11, flexShrink: 0,
            background: 'rgba(255,255,255,0.18)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <FiSmartphone size={22} style={{ color: '#fff' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}>
              Get the Android app
            </div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 500 }}>
              Download the latest version
            </div>
          </div>
          <FiDownload size={20} style={{ color: '#fff', flexShrink: 0 }} />
        </motion.a>

        <p style={{ marginTop: 20, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
          Want your own server?{' '}
          <a href="https://mikesapphub.com" target="_blank" rel="noopener noreferrer"
            style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
            mikesapphub.com
          </a>
        </p>
        <p style={{ marginTop: 10, textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', opacity: 0.7 }}>
          Powered by Asset Logic
        </p>
      </motion.div>
    </div>
  );
}
